import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { User, Calendar, MapPin, Ruler, Weight, Target } from "lucide-react";
import { getPlayerCareer, PlayerCareer } from "@/data/careerStats";
import type { GameEvent } from "@/lib/apiSports";

export interface GameContext {
  league?: string;
  homeTeam?: string;
  awayTeam?: string;
  events?: GameEvent[];
}

interface PlayerProfileModalProps {
  playerName: string | null;
  league?: string;
  gameContext?: GameContext;
  isOpen: boolean;
  onClose: () => void;
}

const PlayerProfileModal = ({ playerName, league, gameContext, isOpen, onClose }: PlayerProfileModalProps) => {
  const player = playerName ? getPlayerCareer(playerName, league || gameContext?.league) : null;

  // Build game-level stats for players not in local DB
  const gameStats = !player && playerName && gameContext?.events
    ? buildGameStats(playerName, gameContext)
    : null;

  const getPositionLabel = (pos: string) => {
    switch (pos) {
      case "C": return "Center";
      case "LW": return "Left Wing";
      case "RW": return "Right Wing";
      case "LD": return "Left Defense";
      case "RD": return "Right Defense";
      case "G": return "Goaltender";
      default: return pos;
    }
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    if (Number.isNaN(birth.getTime())) return "-";
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-card border-border">
        {player ? (
          <FullPlayerProfile
            player={player}
            getPositionLabel={getPositionLabel}
            calculateAge={calculateAge}
          />
        ) : gameStats ? (
          <GameContextProfile
            playerName={playerName!}
            league={league}
            stats={gameStats}
          />
        ) : (
          <div className="py-12 text-center">
            <User className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-display text-lg font-bold mb-2">Player Stats Not Available</h3>
            <p className="text-muted-foreground text-sm">
              We couldn&apos;t find profile data for this player yet.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Full profile for players in local DB ----------

function FullPlayerProfile({
  player,
  getPositionLabel,
  calculateAge,
}: {
  player: PlayerCareer;
  getPositionLabel: (pos: string) => string;
  calculateAge: (birthDate: string) => number | string;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 font-display text-2xl">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          {player.name}
        </DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Nationality:</span>
          <span className="font-medium">{player.nationality}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Age:</span>
          <span className="font-medium">{calculateAge(player.birthDate)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Target className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Position:</span>
          <Badge variant="secondary">{getPositionLabel(player.position)}</Badge>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Ruler className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Height:</span>
          <span className="font-medium">{player.height}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Weight className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Weight:</span>
          <span className="font-medium">{player.weight}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Shoots:</span>
          <span className="font-medium">{player.shoots === "L" ? "Left" : "Right"}</span>
        </div>
      </div>

      <div className="glass-card p-4 bg-primary/5 mb-4">
        <h4 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
          Career Totals
        </h4>
        <div className="grid grid-cols-5 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-primary">{player.careerTotals.gp}</div>
            <div className="text-xs text-muted-foreground">GP</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{player.careerTotals.goals}</div>
            <div className="text-xs text-muted-foreground">Goals</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{player.careerTotals.assists}</div>
            <div className="text-xs text-muted-foreground">Assists</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary">{player.careerTotals.points}</div>
            <div className="text-xs text-muted-foreground">Points</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{player.careerTotals.pim}</div>
            <div className="text-xs text-muted-foreground">PIM</div>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
          Season by Season
        </h4>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="text-muted-foreground">Season</TableHead>
                <TableHead className="text-muted-foreground">Team</TableHead>
                <TableHead className="text-muted-foreground">League</TableHead>
                <TableHead className="text-center text-muted-foreground">GP</TableHead>
                <TableHead className="text-center text-muted-foreground">G</TableHead>
                <TableHead className="text-center text-muted-foreground">A</TableHead>
                <TableHead className="text-center text-muted-foreground">PTS</TableHead>
                <TableHead className="text-center text-muted-foreground">+/-</TableHead>
                <TableHead className="text-center text-muted-foreground">PIM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {player.seasons.map((season) => (
                <TableRow key={`${season.season}-${season.team}`} className="border-border/30 hover:bg-muted/20">
                  <TableCell className="font-medium">{season.season}</TableCell>
                  <TableCell>{season.team}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{season.league}</Badge>
                  </TableCell>
                  <TableCell className="text-center">{season.gp}</TableCell>
                  <TableCell className="text-center">{season.goals}</TableCell>
                  <TableCell className="text-center">{season.assists}</TableCell>
                  <TableCell className="text-center font-bold text-primary">{season.points}</TableCell>
                  <TableCell className={`text-center ${season.plusMinus > 0 ? "text-green-500" : season.plusMinus < 0 ? "text-red-500" : ""}`}>
                    {season.plusMinus > 0 ? `+${season.plusMinus}` : season.plusMinus}
                  </TableCell>
                  <TableCell className="text-center">{season.pim}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}

// ---------- Game-context profile for unknown players ----------

interface GamePlayerStats {
  goals: number;
  assists: number;
  points: number;
  team: string;
  goalDetails: string[];
}

function buildGameStats(playerName: string, ctx: GameContext): GamePlayerStats | null {
  if (!ctx.events || ctx.events.length === 0) return null;

  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();

  const pName = normalize(playerName);
  const pParts = pName.split(" ").filter(Boolean);
  const pLast = pParts[pParts.length - 1] || "";
  const pFirst = pParts[0] || "";
  const abbreviated = pParts.length >= 2 && pFirst.length <= 2;

  const nameMatches = (eventName: string) => {
    const en = normalize(eventName);
    if (en === pName) return true;
    const eParts = en.split(" ").filter(Boolean);
    const eLast = eParts[eParts.length - 1] || "";
    if (eLast === pLast) {
      if (abbreviated) return true; // "R. Ahcan" matches any "X Ahcan"
      return en === pName;
    }
    return false;
  };

  let goals = 0;
  let assists = 0;
  let team = "";
  const goalDetails: string[] = [];

  for (const event of ctx.events) {
    // Events are already filtered to goals by useGameEvents

    const scorerName = event.players?.[0] || "";
    if (nameMatches(scorerName)) {
      goals++;
      team = team || event.team?.name || "";
      goalDetails.push(`${event.period || "?"} - ${event.minute || event.time || "?"}`);
    }

    if (event.assists) {
      for (const assist of event.assists) {
        if (nameMatches(assist)) {
          assists++;
          team = team || event.team?.name || "";
        }
      }
    }
  }

  if (goals === 0 && assists === 0) return null;

  return {
    goals,
    assists,
    points: goals + assists,
    team,
    goalDetails,
  };
}

function GameContextProfile({
  playerName,
  league,
  stats,
}: {
  playerName: string;
  league?: string;
  stats: GamePlayerStats;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 font-display text-2xl">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          {playerName}
        </DialogTitle>
      </DialogHeader>

      <div className="flex items-center gap-2 py-2">
        {stats.team && <Badge variant="secondary">{stats.team}</Badge>}
        {league && <Badge variant="outline">{league}</Badge>}
      </div>

      <div className="glass-card p-4 bg-primary/5 mb-4">
        <h4 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
          This Game
        </h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-foreground">{stats.goals}</div>
            <div className="text-xs text-muted-foreground">Goals</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{stats.assists}</div>
            <div className="text-xs text-muted-foreground">Assists</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary">{stats.points}</div>
            <div className="text-xs text-muted-foreground">Points</div>
          </div>
        </div>
      </div>

      {stats.goalDetails.length > 0 && (
        <div className="space-y-1">
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-2">
            Goal Times
          </h4>
          {stats.goalDetails.map((detail, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-muted/50 border border-border/50">
              <span className="text-lg">🏒</span>
              <span>{detail}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground/60 mt-4 text-center italic">
        Full career stats are available for select players in our database.
      </p>
    </>
  );
}

export default PlayerProfileModal;
